import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../state";
import { Navbar, type NavBalance } from "../components/navbar";
import type { Ninja, LedgerTxn, TxnType, LedgerResponse, BalanceResponse } from "../types";
import { get } from "../api/client";
import i18n from "../i18n";
import { formatDateTime } from "../utils/date";

const TXN_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
};

function getTxnDateLabel(txn: LedgerTxn): string {
  if (txn.type === "INITIAL_BALANCE") return i18n.t("dashboard.startingBalance");
  return formatDateTime(txn.createdAt, TXN_DATE_OPTIONS);
}

const LEDGER_PAGE_SIZE = 10;

function getTxnIcon(type: TxnType): string {
  switch (type) {
    case "INITIAL_BALANCE":
      return "+";
    case "ACTIVITY_REWARD":
      return "+";
    case "PURCHASE":
      return "-";
    case "ADJUSTMENT":
      return "~";
    default:
      return "";
  }
}

function getTxnClass(amount: number): string {
  if (amount > 0) return "positive";
  if (amount < 0) return "negative";
  return "neutral";
}

function TransactionList({
  transactions,
  error,
}: {
  transactions: LedgerTxn[] | null;
  error: boolean;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  if (error) {
    return <li className="error">{t("dashboard.ledgerError")}</li>;
  }
  if (transactions === null) {
    return <li className="loading">{t("dashboard.ledgerLoading")}</li>;
  }
  if (transactions.length === 0) {
    return <li className="empty">{t("dashboard.noTransactions")}</li>;
  }

  const start = (page - 1) * LEDGER_PAGE_SIZE;
  const pageTransactions = transactions.slice(start, start + LEDGER_PAGE_SIZE);
  const totalPages = Math.ceil(transactions.length / LEDGER_PAGE_SIZE);

  return (
    <>
      {pageTransactions.map((txn) => (
        <li key={txn.id} className={`transaction-item ${getTxnClass(txn.amount)}`}>
          <span className="transaction-icon">{getTxnIcon(txn.type)}</span>
          <div className="transaction-details">
            <span className="transaction-desc">{txn.description}</span>
            <span className="transaction-date">{getTxnDateLabel(txn)}</span>
          </div>
          <span className="transaction-amount">
            {txn.amount > 0 ? "+" : ""}
            {txn.amount}
          </span>
        </li>
      ))}
      {totalPages > 1 && (
        <li className="pagination">
          <button
            className="btn-page btn-prev"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            {t("common.prev")}
          </button>
          <span className="page-info">
            {page} / {totalPages}
          </span>
          <button
            className="btn-page btn-next"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            {t("common.next")}
          </button>
        </li>
      )}
    </>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { facilityId, studentId, currentNinja, setCurrentNinja } = useAuth();

  // The cached profile (if any) renders the page shell immediately; balance and
  // ledger fill in asynchronously so the student can navigate without waiting.
  const [ninja, setNinja] = useState<Ninja | null>(currentNinja);
  const [profileError, setProfileError] = useState(false);
  // null until either the dedicated balance call or the profile fetch fills it.
  const [balance, setBalance] = useState<number | null>(null);
  const [balanceError, setBalanceError] = useState(false);
  const [transactions, setTransactions] = useState<LedgerTxn[] | null>(null);
  const [ledgerError, setLedgerError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const hadCachedProfile = currentNinja !== null;

    async function loadProfile() {
      const res = await get<Ninja>(`/facilities/${facilityId}/ninjas/${studentId}`);
      if (cancelled) return;
      if (!res.data) {
        // Without a cached profile there is nothing to show — surface the error.
        if (!hadCachedProfile) setProfileError(true);
        return;
      }
      setNinja(res.data);
      setCurrentNinja(res.data);
      // The profile carries the live balance too; use it as a fallback so a
      // failed (or slower) balance call still ends up with a number.
      setBalance((prev) => prev ?? res.data!.currentBalance ?? 0);
      setBalanceError(false);
    }

    async function loadBalance() {
      const res = await get<BalanceResponse>(
        `/facilities/${facilityId}/ninjas/${studentId}/balance`,
      );
      if (cancelled) return;
      if (res.data) {
        setBalance(res.data.currentBalance);
        setBalanceError(false);
      } else {
        setBalanceError(true);
      }
    }

    async function loadLedger() {
      // Spring Pageable: the param is `size`; `limit` was silently ignored and
      // the endpoint fell back to the 20-row default page.
      const res = await get<LedgerResponse>(
        `/facilities/${facilityId}/ninjas/${studentId}/ledger?size=100`,
      );
      if (cancelled) return;
      if (res.data) {
        setTransactions(res.data.transactions ?? []);
        setLedgerError(false);
      } else {
        setTransactions([]);
        setLedgerError(true);
      }
    }

    // Cached belt/level/name may be stale; always refresh the profile. With a
    // cache the dedicated balance call runs in parallel for the fastest number.
    void loadProfile();
    if (hadCachedProfile) void loadBalance();
    void loadLedger();

    return () => {
      cancelled = true;
    };
    // Refetch only when the session changes, not when the profile cache updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, studentId]);

  // Sync the freshest balance back into the cached profile so the navbar on
  // other pages shows it too.
  useEffect(() => {
    if (balance === null || balanceError) return;
    const cached = currentNinja;
    if (cached && cached.currentBalance !== balance) {
      setCurrentNinja({ ...cached, currentBalance: balance });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balance, balanceError]);

  const navBalance: NavBalance = balanceError
    ? "error"
    : balance === null
      ? "loading"
      : balance;

  if (!ninja) {
    return (
      <>
        <Navbar balance={navBalance} />
        <main className="dashboard-page">
          {profileError ? (
            <div className="error">{t("dashboard.profileError")}</div>
          ) : (
            <div className="loading">{t("dashboard.loadingDashboard")}</div>
          )}
        </main>
      </>
    );
  }

  const levelLabel =
    ninja.levelSequence != null
      ? t("dashboard.level", { number: ninja.levelSequence })
      : ninja.levelName || t("common.unknown");

  return (
    <>
      <Navbar balance={navBalance} />
      <main className="dashboard-page">
        <div className="dashboard-container">
          <div className="dashboard">
            <header className="dashboard-header">
              <h1>
                {t("dashboard.welcome", {
                  name: [ninja.firstName, ninja.lastName].filter(Boolean).join(" "),
                })}
              </h1>
              <div className="ninja-info">
                <span
                  className={`belt-badge belt-${(ninja.courseName ?? "unknown").toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {ninja.courseName
                    ? t(`belts.${ninja.courseName}`, { defaultValue: ninja.courseName })
                    : t("common.unknown")}
                </span>
                <span className="level-badge">{levelLabel}</span>
              </div>
            </header>

            <div className="balance-card">
              <div className="balance-label">{t("dashboard.yourBalance")}</div>
              <div className="balance-amount">
                {balanceError ? (
                  <>
                    <span className="balance-error">—</span>{" "}
                    <span className="balance-unit">{t("common.buxUnit")}</span>
                  </>
                ) : balance === null ? (
                  <>
                    <span className="balance-loading">…</span>{" "}
                    <span className="balance-unit">{t("common.buxUnit")}</span>
                  </>
                ) : (
                  <>
                    {balance} <span className="balance-unit">{t("common.buxUnit")}</span>
                  </>
                )}
              </div>
            </div>

            <div className="quick-links">
              <Link to="/shop" className="btn btn-primary">
                {t("dashboard.spendYourBux")}
              </Link>
              <Link to="/leaderboard" className="btn btn-secondary">
                {t("dashboard.leaderboard")}
              </Link>
            </div>

            <section className="recent-activity">
              <h2>{t("dashboard.yourLedger")}</h2>
              <ul className="transaction-list">
                <TransactionList transactions={transactions} error={ledgerError} />
              </ul>
            </section>
          </div>
        </div>
      </main>
    </>
  );
}
